import { CommonModule } from '@angular/common'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { NbButtonModule, NbCardModule, NbFormFieldModule, NbIconModule, NbInputModule, NbSpinnerModule, NbTooltipModule } from '@nebular/theme'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { EmailActionTemplate } from '../../interfaces/email-action.interface'
import { EmailComposerStateService } from '../../services/email-composer-state.service'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-actions-manager',
  template: `
    <nb-card>
      <nb-card-header class="header">
        <div class="left">
          <h3>Actions</h3>
        </div>
        <div class="right">
          <div class="search-pill">
            <nb-icon class="prefix" icon="search-outline"></nb-icon>
            <input type="text" [ngModel]="search" (ngModelChange)="reload()" placeholder="Search actions..." />
            <button class="clear-btn" *ngIf="search" (click)="search = ''; reload()" nbTooltip="Clear" nbTooltipPlacement="bottom">
              <nb-icon icon="close-outline"></nb-icon>
            </button>
          </div>
          <button class="primary" nbButton size="small" status="basic" (click)="openCreate()">Create Action</button>
        </div>
      </nb-card-header>
      <nb-card-body>
        <div class="list" *ngIf="actions?.length; else empty">
          <div class="row" *ngFor="let a of actions">
            <div class="info">
              <div class="title">{{ a.action }}</div>
              <div class="subtitle">Template: {{ a.email_template?.name }} — Subject: {{ a.email_template?.subject }}</div>
            </div>
            <div class="actions">
              <button nbButton size="small" status="basic" (click)="openEdit(a)">Edit</button>
              <button nbButton size="small" status="danger" ghost (click)="confirmDelete(a)">Delete</button>
            </div>
          </div>
        </div>
        <ng-template #empty>
          <div class="empty">
            <p>No actions found.</p>
          </div>
        </ng-template>
      </nb-card-body>
    </nb-card>
  `,
  styles: [
    `
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
      }
      .right {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .search-pill {
        display: flex;
        align-items: center;
        gap: 8px;
        background: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 999px;
        padding: 6px 12px;
      }
      .search-pill .prefix {
        color: #9ca3af;
      }
      .search-pill input {
        border: none;
        outline: none;
        background: transparent;
        width: 260px;
        font-size: 14px;
      }
      .clear-btn {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        border: none;
        background: transparent;
        cursor: pointer;
      }
      .clear-btn:hover {
        background: #eef2ff;
      }
      button.primary {
        background: #2563eb !important;
        color: #fff !important;
        border: 1px solid #2563eb !important;
      }
      .row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px;
        border: 1px solid #eee;
        border-radius: 8px;
        margin-bottom: 8px;
      }
      .title {
        font-weight: 600;
      }
      .subtitle {
        color: #666;
        font-size: 12px;
      }
      .filters {
        margin-bottom: 12px;
      }
    `
  ],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NbCardModule, NbButtonModule, NbIconModule, NbFormFieldModule, NbInputModule, NbSpinnerModule, NbTooltipModule]
})
export class ActionsManagerComponent implements OnInit, OnDestroy {
  @Output() editAction = new EventEmitter<number>()

  private destroy$ = new Subject<void>()
  actions: EmailActionTemplate[] = []
  search = ''

  constructor(private service: EmailComposerService, private state: EmailComposerStateService) {}

  ngOnInit(): void {
    this.reload()
    // React to global search (debounced) and keep in sync
    this.state.search$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.search = value || ''
      this.reload()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  reload(): void {
    this.service
      .listActions({ search: this.search || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => (this.actions = res.data || []))
  }

  openCreate(): void {
    this.editAction.emit(0)
  }

  openEdit(a: EmailActionTemplate): void {
    this.editAction.emit(a.id)
  }

  confirmDelete(a: EmailActionTemplate): void {
    const ok = window.confirm(`Delete action "${a.action}"?`)
    if (!ok) return
    this.service
      .deleteAction(a.id!)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reload())
  }
}
